import React, { useState } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import {
  ContentActions,
  APPROVE_ACTION_COLORS,
  REJECT_ACTION_COLORS,
} from "@/components/ui/ContentActions";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import type { FinancialSettlement } from "@/lib/api/review";
import {
  ReviewField,
  FieldRow,
  Divider,
  FieldDecisionActions,
  type FieldDecision,
  type RejectFieldMeta,
  type FieldRejectionData,
  type EditFieldMeta,
  type FieldEditData,
} from "./Shared";
import { TranslationKeys } from "@chinese-vendor/i18n";
import { Modal } from "@/components/ui/Modal";
import { FilePreview } from "@/components/ui/FilePreview";

// ─── QR Code modal ────────────────────────────────────────────────────────────

function QrCodeModal({
  visible,
  onDismiss,
  qrCodeUrl,
  titleKey,
  applicationId,
}: {
  visible: boolean;
  onDismiss: () => void;
  qrCodeUrl: string;
  titleKey: TranslationKeys;
  applicationId: string;
}) {
  return (
    <Modal visible={visible} onDismiss={onDismiss} title={titleKey}>
      <FilePreview fileKey={qrCodeUrl} isVendor={false} applicationId={applicationId} />
    </Modal>
  );
}

interface FinancialSettlementSectionProps {
  settlement: FinancialSettlement | undefined;
  fieldDecisions: Record<string, FieldDecision>;
  fieldRejectionData: Record<string, FieldRejectionData>;
  onDecide: (fieldKey: string, decision: FieldDecision) => void;
  onRejectField: (fieldKey: string, meta: RejectFieldMeta) => void;
  onUndoDecision: (fieldKey: string) => void;
  onShowReason: (fieldKey: string) => void;
  fieldEditData: Record<string, FieldEditData>;
  onEditField: (fieldKey: string, meta: EditFieldMeta) => void;
  onShowEdit: (fieldKey: string) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function FinancialSettlementSection({
  settlement,
  fieldDecisions,
  fieldRejectionData: _fieldRejectionData,
  onDecide,
  onRejectField,
  onUndoDecision,
  onShowReason,
  fieldEditData: _fieldEditData,
  onEditField: _onEditField,
  onShowEdit,
  applicationId,
  t,
}: FinancialSettlementSectionProps) {
  const viewOnly = t("review.review.viewOnly");
  const notProvided = t("review.application.financialSettlement.notProvided");
  const [wechatQrVisible, setWechatQrVisible] = useState(false);

  function SubSectionHeader({ label }: { label: string }) {
    return (
      <AppText weight="bold" className="text-xs text-dark-text-15 uppercase tracking-widest mb-6">
        {label}
      </AppText>
    );
  }

  return (
    <View className="items-start w-full">
      {/* Card header */}
      <View className="px-6 py-5 border-b border-stroke-50 items-start w-full">
        <AppText weight="semibold" className="text-lg text-dark-text-50">
          {t("review.application.financialSettlement.title")}
        </AppText>
      </View>

      {/* Card body */}
      <View className="px-8 py-6 w-full">
        {/* ── BANK ──────────────────────────────────────────────── */}
        {settlement?.bank && (
          <View className="items-start w-full">
            <SubSectionHeader label={t("review.application.financialSettlement.sections.bank")} />

            {/* Account Holder Name — VIEW ONLY, full width */}
            <ReviewField
              label={t("review.application.financialSettlement.fields.accountHolderName")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {settlement.bank.accountHolderName ?? notProvided}
              </AppText>
            </ReviewField>

            <Divider />

            {/* Bank Account Number */}
            <ReviewField
              label={t("review.application.financialSettlement.fields.bankAccountNumber")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {settlement.bank.accountNumber}
              </AppText>
            </ReviewField>

            <Divider />

            {/* IBAN + Swift Code — VIEW ONLY, side by side */}
            <FieldRow>
              <ReviewField
                label={t("review.application.financialSettlement.fields.iban")}
                viewOnlyLabel={viewOnly}
              >
                <AppText weight="medium" className="text-lg text-dark-text-50">
                  {settlement.bank.iban ?? notProvided}
                </AppText>
              </ReviewField>
              <ReviewField
                label={t("review.application.financialSettlement.fields.swiftCode")}
                viewOnlyLabel={viewOnly}
              >
                <AppText weight="medium" className="text-lg text-dark-text-50">
                  {settlement.bank.swiftCode}
                </AppText>
              </ReviewField>
            </FieldRow>

            <Divider />

            {/* Bank Address — VIEW ONLY, full width */}
            <ReviewField
              label={t("review.application.financialSettlement.fields.bankAddress")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {settlement.bank.bankAddress}
              </AppText>
            </ReviewField>

            <Divider />

            {/* Settlement Currency — VIEW ONLY, full width */}
            <ReviewField
              label={t("review.application.financialSettlement.fields.settlementCurrency")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {settlement.bank.settlementCurrency}
              </AppText>
            </ReviewField>
          </View>
        )}

        {settlement?.bank && (settlement?.alipay || settlement?.wechatPay) && <Divider />}

        {/* ── ALIPAY ──────────────────────────────────────────────── */}
        {settlement?.alipay && (
          <View className="items-start w-full">
            <SubSectionHeader label={t("review.application.financialSettlement.sections.alipay")} />

            {/* Alipay Merchant ID — Approve/Reject */}
            <View className="gap-3 items-start w-full">
              <AppText weight="semibold" className="text-base text-supporting-text-40">
                {t("review.application.financialSettlement.fields.alipayMerchantId")}
              </AppText>
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {settlement.alipay.merchantId}
              </AppText>
              {fieldDecisions["alipayMerchantId"] ? (
                <FieldDecisionActions
                  fieldKey="alipayMerchantId"
                  decision={fieldDecisions["alipayMerchantId"]}
                  onUndoDecision={onUndoDecision}
                  onShowReason={onShowReason}
                  onShowEdit={onShowEdit}
                  t={t}
                />
              ) : (
                <ContentActions
                  actions={[
                    {
                      label: "review.application.approve",
                      onPress: () => onDecide("alipayMerchantId", "approve"),
                      icon: (
                        <Icon
                          as={Feather}
                          name="check-circle"
                          size={14}
                          className="text-green-50"
                        />
                      ),
                      ...APPROVE_ACTION_COLORS,
                    },
                    {
                      label: "review.application.reject",
                      onPress: () =>
                        onRejectField("alipayMerchantId", {
                          label: t(
                            "review.application.financialSettlement.fields.alipayMerchantId"
                          ),
                          value: settlement.alipay?.merchantId,
                        }),
                      icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                      ...REJECT_ACTION_COLORS,
                    },
                  ]}
                />
              )}
            </View>

            <Divider />

            {/* Alipay Business Name + Account Email — VIEW ONLY, side by side */}
            <FieldRow>
              <ReviewField
                label={t("review.application.financialSettlement.fields.alipayBusinessName")}
                viewOnlyLabel={viewOnly}
              >
                <AppText weight="medium" className="text-lg text-dark-text-50">
                  {settlement.alipay.businessName}
                </AppText>
              </ReviewField>
              <ReviewField
                label={t("review.application.financialSettlement.fields.alipayEmail")}
                viewOnlyLabel={viewOnly}
              >
                <AppText weight="medium" className="text-lg text-dark-text-50">
                  {settlement.alipay.email}
                </AppText>
              </ReviewField>
            </FieldRow>

            <Divider />

            {/* Settlement Currency — VIEW ONLY, full width */}
            <ReviewField
              label={t("review.application.financialSettlement.fields.settlementCurrency")}
              viewOnlyLabel={viewOnly}
            >
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {settlement.alipay.settlementCurrency}
              </AppText>
            </ReviewField>

            {/* Alipay QR Code — VIEW ONLY with Show QR Code button */}
            {settlement.alipay.qrCodeUrl && (
              <>
                <Divider />
                <ReviewField
                  label={t("review.application.financialSettlement.fields.alipayQrCode")}
                  viewOnlyLabel={viewOnly}
                >
                  <Button
                    label="review.application.financialSettlement.showQrCode"
                    onPress={() => {}}
                    variant="primary"
                    className="self-start"
                  />
                </ReviewField>
              </>
            )}
          </View>
        )}

        {settlement?.alipay && settlement?.wechatPay && <Divider />}

        {/* ── WECHAT PAY ──────────────────────────────────────────── */}
        {settlement?.wechatPay && (
          <View className="items-start w-full">
            <SubSectionHeader
              label={t("review.application.financialSettlement.sections.wechatPay")}
            />

            {/* WeChat Pay Merchant ID — Approve/Reject */}
            <View className="gap-3 items-start w-full">
              <AppText weight="semibold" className="text-base text-supporting-text-40">
                {t("review.application.financialSettlement.fields.wechatMerchantId")}
              </AppText>
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {settlement.wechatPay.merchantId}
              </AppText>
              {fieldDecisions["wechatMerchantId"] ? (
                <FieldDecisionActions
                  fieldKey="wechatMerchantId"
                  decision={fieldDecisions["wechatMerchantId"]}
                  onUndoDecision={onUndoDecision}
                  onShowReason={onShowReason}
                  onShowEdit={onShowEdit}
                  t={t}
                />
              ) : (
                <ContentActions
                  actions={[
                    {
                      label: "review.application.approve",
                      onPress: () => onDecide("wechatMerchantId", "approve"),
                      icon: (
                        <Icon
                          as={Feather}
                          name="check-circle"
                          size={14}
                          className="text-green-50"
                        />
                      ),
                      ...APPROVE_ACTION_COLORS,
                    },
                    {
                      label: "review.application.reject",
                      onPress: () =>
                        onRejectField("wechatMerchantId", {
                          label: t(
                            "review.application.financialSettlement.fields.wechatMerchantId"
                          ),
                          value: settlement.wechatPay?.merchantId,
                        }),
                      icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                      ...REJECT_ACTION_COLORS,
                    },
                  ]}
                />
              )}
            </View>

            <Divider />

            {/* WeChat Pay Business Name + Account Email — VIEW ONLY, side by side */}
            <FieldRow>
              <ReviewField
                label={t("review.application.financialSettlement.fields.wechatBusinessName")}
                viewOnlyLabel={viewOnly}
              >
                <AppText weight="medium" className="text-lg text-dark-text-50">
                  {settlement.wechatPay.businessName}
                </AppText>
              </ReviewField>
              <ReviewField
                label={t("review.application.financialSettlement.fields.wechatEmail")}
                viewOnlyLabel={viewOnly}
              >
                <AppText weight="medium" className="text-lg text-dark-text-50">
                  {settlement.wechatPay.email}
                </AppText>
              </ReviewField>
            </FieldRow>

            <Divider />

            {/* Settlement Account Type + Settlement Currency — VIEW ONLY, side by side */}
            <FieldRow>
              <ReviewField
                label={t(
                  "review.application.financialSettlement.fields.wechatSettlementAccountType"
                )}
                viewOnlyLabel={viewOnly}
              >
                <AppText weight="medium" className="text-lg text-dark-text-50">
                  {settlement.wechatPay.settlementAccountType}
                </AppText>
              </ReviewField>
              <ReviewField
                label={t("review.application.financialSettlement.fields.settlementCurrency")}
                viewOnlyLabel={viewOnly}
              >
                <AppText weight="medium" className="text-lg text-dark-text-50">
                  {settlement.wechatPay.settlementCurrency}
                </AppText>
              </ReviewField>
            </FieldRow>

            {/* WeChat Pay QR Code — VIEW ONLY with Show QR Code button */}
            {settlement.wechatPay.qrCodeUrl && (
              <>
                <Divider />
                <ReviewField
                  label={t("review.application.financialSettlement.fields.wechatQrCode")}
                  viewOnlyLabel={viewOnly}
                >
                  <Button
                    label="review.application.financialSettlement.showQrCode"
                    onPress={() => setWechatQrVisible(true)}
                    variant="primary"
                    className="self-start"
                  />
                </ReviewField>
              </>
            )}
          </View>
        )}
      </View>

      {settlement?.wechatPay?.qrCodeUrl && (
        <QrCodeModal
          visible={wechatQrVisible}
          onDismiss={() => setWechatQrVisible(false)}
          qrCodeUrl={settlement.wechatPay.qrCodeUrl}
          titleKey="review.application.financialSettlement.fields.wechatQrCode"
          applicationId={applicationId}
        />
      )}
    </View>
  );
}
